import { useEffect, useState } from 'react'

import posthog from 'src/utils/pixels/posthog'
import { getWaSessionToken } from 'src/utils/waSessionToken'

/**
 * PostHog-ready read of the WhatsApp session token for embedding in a
 * prefill URL that is built at RENDER time.
 *
 * Two problems this solves that a bare `getWaSessionToken()` at render does
 * not:
 *
 * 1. **Hydration.** The token is absent during SSR but could be present on
 *    the client, and it feeds an SSR'd `<a href>` — reading it at render
 *    would diverge server vs client HTML → React 19 #418 (see the hydration
 *    history in CLAUDE.md). Returning `''` until after mount keeps SSR and
 *    the first client render identical; the token folds in on a normal
 *    post-mount state update React never compares against the server HTML.
 *    Same rationale as `useClickIdsForPrefill`.
 *
 * 2. **Coverage — the reason this hook exists at all.** `posthog.sessionId()`
 *    returns `''` until the lazy `posthog-js` import resolves behind the 2 s
 *    `useDelayedLoad` fuse in `Scripts.tsx`. A render-time read therefore
 *    lands on `''` at first paint *by construction*, and a plain mount
 *    `useEffect` is no better — it runs at hydration, still seconds early. So
 *    the prefill shipped with NO token unless something happened to re-render
 *    the component after PostHog woke up, silently breaking the closed loop
 *    for a large share of sessions. `posthog.onReady` waits for the client to
 *    actually be live, then re-renders with a real token.
 *
 * Deliberately does NOT alias — that fires on click (`aliasWaSession`), not
 * on mount, so we don't identify every listing viewer. See `waSessionToken`.
 *
 * Click-handler call sites (PriceCard, Header, LeadFormInlineCta) run only on
 * user interaction, long after the fuse, so they skip this hook and call
 * `claimWaSessionToken()` directly.
 */
export function useWaSessionToken(): string {
  const [token, setToken] = useState('')

  useEffect(() => {
    let cancelled = false
    posthog.onReady(() => {
      if (cancelled) return
      setToken(getWaSessionToken())
    })
    return () => {
      cancelled = true
    }
  }, [])

  return token
}
